import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Measures an element and re-measures whenever it resizes.
 *
 * For a box whose height something else has to allow for - the chat's floating
 * header and composer both publish theirs so the conversation can pad itself
 * clear of them. Measured rather than assumed, because a reply strip, a row of
 * staged files or a multi-line draft each change it.
 *
 * Null until the first measurement, so a caller can tell "not measured yet"
 * from "measured as zero" and fall back to a sensible default in the meantime.
 *
 * A CALLBACK ref, not a ref object read from an effect - which is what this
 * used to be, and the reason a thread opened on a phone kept its last message
 * behind the composer. The admin page mounts this component before a
 * conversation is picked, when there is no composer to measure; the effect ran
 * once against a null ref, nothing re-ran it when the composer finally
 * appeared, and the height stayed unmeasured for the life of the page. The
 * conversation then fell back to a guessed 64px against a real 74, so the last
 * message sat under the composer with no scroll left to reach it - and a reload
 * "fixed" it only because the id was in the URL from the start, so the composer
 * existed on the first render. React calls this on every node change, so
 * whenever the element arrives, that is when it is measured.
 *
 * `enabled` is for an element that only exists some of the time: flipping it
 * re-runs the attachment, so a caller can also gate the observer explicitly.
 */
export const useElementHeight = <T extends HTMLElement>(enabled = true) => {
  const [height, setHeight] = useState<number | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node || !enabled) return;
      // Straight away, so the first paint after the element appears already has
      // a real number rather than the caller's fallback.
      setHeight(node.offsetHeight);

      if (typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(() => setHeight(node.offsetHeight));
      observer.observe(node);
      observerRef.current = observer;
    },
    [enabled]
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { ref, height };
};
